import { Box } from '@mui/material';
import { EvaluationType, FilterKeys } from '@schooly/api';
import {
  EvaluationTypeSelectMultiple,
  SubjectSelectMultiple,
  toggleArrayProperty,
} from '@schooly/components/filters';
import { TagSelect } from '@schooly/style';
import React, { FC, useCallback } from 'react';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { MarkbookFilterForm } from '../../../../../context/markbook/MarkbookFilterContext';
import { useSchool } from '../../../../../hooks/useSchool';
import { useConfirmAction } from '../helpers';
import { StepWrapper } from '../MarkbookFilterModal';

export const SelectAssessmentsStep: FC = () => {
  const { control, watch, trigger, setValue } = useFormContext<MarkbookFilterForm>();
  const subjects = watch(FilterKeys.Subject);
  const aolEvaluationType = watch('aol_evaluation_type');
  const selectedAssessmentIds = watch('selectedAssessmentIds');
  const { schoolId = '' } = useSchool();
  const { formatMessage } = useIntl();
  const hasAssessmentSelections = selectedAssessmentIds === 'all' || selectedAssessmentIds.size > 0;

  const tutorGroup = watch('tutor_group');

  const withConfirmation = useConfirmAction({
    shouldConfirm: hasAssessmentSelections,
    afterConfirm: () => {
      setValue('selectedAssessmentIds', new Set());
    },
  });

  const validateAssessmentCriteria = (
    subjectValue: string[],
    aolValue: [string[], EvaluationType[]],
    tutorGroup: boolean,
  ) =>
    subjectValue.length > 0 ||
    aolValue[0]?.length > 0 ||
    aolValue[1]?.length > 0 ||
    tutorGroup ||
    formatMessage({
      id: 'assessments-markbook-AtLeastOneAssessmentCriterion',
    });

  const handleSelection = useCallback(
    async (shouldConfirm: boolean, updateAction: () => void) => {
      if (shouldConfirm) {
        await withConfirmation(async () => {
          updateAction();
          trigger();
        });
      } else {
        updateAction();
        trigger();
      }
    },
    [withConfirmation, trigger],
  );

  const handleSubjectSelection = useCallback(
    async (
      field: ControllerRenderProps<MarkbookFilterForm, FilterKeys.Subject>,
      subjectId: string,
    ) => {
      const isRemoving = field.value.includes(subjectId);

      await handleSelection(isRemoving, () =>
        field.onChange(toggleArrayProperty(field.value, subjectId)),
      );
    },
    [handleSelection],
  );

  const handleAreaOfLearningSelection = useCallback(
    async (
      field: ControllerRenderProps<MarkbookFilterForm, 'aol_evaluation_type'>,
      aolId: string,
    ) => {
      await handleSelection(true, () =>
        field.onChange([toggleArrayProperty(field.value[0] || [], aolId), field.value[1] || []]),
      );
    },
    [handleSelection],
  );

  const handleEvaluationTypeSelection = useCallback(
    async (
      field: ControllerRenderProps<MarkbookFilterForm, 'aol_evaluation_type'>,
      evaluationType: EvaluationType,
    ) => {
      const isRemoving = field.value[1].includes(evaluationType);

      await handleSelection(isRemoving, () =>
        field.onChange([
          field.value[0] || [],
          toggleArrayProperty(field.value[1] || [], evaluationType),
        ]),
      );
    },
    [handleSelection],
  );

  const handleClearAll = useCallback(
    async (field: ControllerRenderProps<MarkbookFilterForm, 'aol_evaluation_type'>) => {
      await withConfirmation(() => {
        field.onChange([[], []]);
      });
    },
    [withConfirmation],
  );
  const hasSubjectOrTutorGroup = tutorGroup || subjects.length > 0;

  return (
    <StepWrapper gap={3} px={2.5}>
      <Controller
        control={control}
        name={FilterKeys.Subject}
        rules={{
          validate: (value: string[]) =>
            validateAssessmentCriteria(value, aolEvaluationType, tutorGroup),
        }}
        render={({ field, fieldState }) => (
          <SubjectSelectMultiple
            error={
              fieldState.error
                ? {
                    ...fieldState.error,
                    messageTextId: 'assessments-markbook-AtLeastOneAssessmentCriterion',
                  }
                : undefined
            }
            schoolId={schoolId}
            onSelectSubjectId={(subjectId) => handleSubjectSelection(field, subjectId)}
            selectedIds={field.value}
            hasValues={hasSubjectOrTutorGroup}
            allowArchived
            placeholder={formatMessage({ id: 'assessments-markbook-subjectTutorGroup' })}
            optionsTitle={formatMessage({ id: 'section-Subjects' })}
            renderAdditionalSelectedItems={(opened) => {
              if (!tutorGroup) return null;
              return (
                <TagSelect
                  size="small"
                  label={formatMessage({ id: 'groups-TutorGroup' })}
                  onDelete={
                    opened
                      ? () => {
                          setValue('tutor_group', false);
                          trigger();
                        }
                      : undefined
                  }
                />
              );
            }}
            renderAdditionalContent={() => {
              return (
                <Box
                  sx={{ cursor: 'pointer', my: 1 }}
                  onClick={() => {
                    setValue('tutor_group', !tutorGroup);
                    trigger();
                  }}
                >
                  <TagSelect
                    variant={!tutorGroup ? 'outlined' : 'filled'}
                    label={formatMessage({ id: 'groups-TutorGroup' })}
                  />
                </Box>
              );
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="aol_evaluation_type"
        rules={{
          validate: (value: [string[], EvaluationType[]]) =>
            validateAssessmentCriteria(subjects, value, tutorGroup),
        }}
        render={({ field, fieldState }) => (
          <EvaluationTypeSelectMultiple
            schoolId={schoolId}
            selectedAreaOfLearningIds={field.value[0] || []}
            selectedEvaluationTypes={field.value[1] || []}
            error={fieldState.error}
            onSelectAreaOfLearning={(aolId) => handleAreaOfLearningSelection(field, aolId)}
            onSelectEvaluationType={(evaluationType) =>
              handleEvaluationTypeSelection(field, evaluationType)
            }
            placeholder={formatMessage({
              id: 'assessments-markbook-AreaOfLearning-TutorsFeedback',
            })}
            onClear={() => handleClearAll(field)}
          />
        )}
      />
    </StepWrapper>
  );
};
